import { useState } from "react";
import { motion } from "framer-motion";
import { BadgeCheck, ExternalLink, Maximize2 } from "lucide-react";
import { GBT_CERT, GBT_FINDER_URL } from "@/content/gbt";
import { CertificateLightbox, CERT_THUMB } from "./CertificateLightbox";

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-4 py-3 border-t border-[#FCFBF7]/10 text-sm">
    <span className="text-[#FCFBF7]/55">{label}</span>
    <span className="text-[#FCFBF7] text-right" data-testid={testid}>{value}</span>
  </div>
);

const CertThumb = ({ onOpen }) => (
  <button type="button" onClick={onOpen} data-testid="gbt-cert-thumb"
    className="group relative shrink-0 w-24 sm:w-28 rotate-[-4deg] hover:rotate-0 hover:scale-105 transition-transform duration-500 ease-out"
    aria-label="Ingrandisci il certificato GBT originale">
    <img src={CERT_THUMB} alt="Certificato GBT originale" loading="lazy" className="w-full rounded-md bg-white shadow-[0_20px_40px_-12px_rgba(0,0,0,0.6)]" />
    <span className="absolute -bottom-2 -right-2 grid place-items-center w-8 h-8 rounded-full bg-[#86C89B] text-[#0F2317] shadow-lg">
      <Maximize2 size={14} />
    </span>
  </button>
);

export const GbtCertificate = () => {
  const [open, setOpen] = useState(false);
  return (
  <>
  <motion.div
    initial={{ opacity: 0, y: 30, rotate: -1.5 }} whileInView={{ opacity: 1, y: 0, rotate: 0 }} viewport={{ once: true }}
    transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
    className="relative rounded-[2rem] bg-[#0F2317] p-8 sm:p-10 overflow-hidden shadow-[0_40px_90px_-40px_rgba(15,35,23,0.8)]"
    data-testid="gbt-certificate"
  >
    <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#86C89B]/15 blur-3xl" />
    <div className="relative flex items-center gap-4">
      <div className="grid place-items-center w-20 h-20 rounded-full border-2 border-[#86C89B] text-center">
        <span className="f-mono text-[9px] leading-tight tracking-[0.15em] text-[#86C89B]">GUIDED<br /><b className="text-[11px] text-[#FCFBF7]">BIOFILM</b><br />THERAPY</span>
      </div>
      <div>
        <p className="eyebrow text-[#86C89B]">Certificato GBT</p>
        <p className="f-serif text-3xl text-[#FCFBF7] leading-tight mt-1">Formazione certificata<br /><em className="text-[#86C89B]">GBT originale</em></p>
      </div>
    </div>
    <div className="relative mt-7 flex items-center gap-6">
      <CertThumb onOpen={() => setOpen(true)} />
      <div>
        <p className="text-[#FCFBF7]/70 text-sm leading-relaxed">
          Il Dott. Nicola Mauro ha completato il corso ufficiale in Guided Biofilm Therapy con AIRFLOW®, PERIOFLOW® e PIEZON®.
        </p>
        <button type="button" onClick={() => setOpen(true)} data-testid="gbt-cert-open-btn"
          className="mt-3 text-sm text-[#86C89B] underline underline-offset-4 decoration-[#86C89B]/40 hover:decoration-[#86C89B]">
          Guarda il certificato originale
        </button>
      </div>
    </div>
    <div className="relative mt-6">
      <Row label="Intestatario" value={GBT_CERT.holder} testid="gbt-cert-holder" />
      <Row label="Certificato n°" value={GBT_CERT.number} testid="gbt-cert-number" />
      <Row label="Conseguito" value={GBT_CERT.obtained} testid="gbt-cert-validity" />
      <Row label="Rilasciato da" value={GBT_CERT.issuer} />
    </div>
    <a href={GBT_FINDER_URL} target="_blank" rel="noopener noreferrer" data-testid="gbt-finder-link"
      className="relative mt-8 inline-flex items-center gap-2 rounded-full bg-[#86C89B] text-[#0F2317] px-6 py-3 text-sm font-medium hover:bg-[#FCFBF7] transition-colors">
      <BadgeCheck size={17} /> Verificalo sul GBT Finder <ExternalLink size={14} />
    </a>
  </motion.div>
  <CertificateLightbox open={open} onClose={() => setOpen(false)} />
  </>
  );
};
